import {
  ChangeEvent,
  FocusEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { fetchPrograms } from 'actions/programs';
import { RATINGS } from 'constants/ponder';
import { StoreType } from 'reducers/types';
import {
  TextInput,
  TypeaheadWrapper,
  SuggestionsList,
  SuggestionItem,
  SuggestionHint,
  ImageRow,
  ImagePreview,
  ImageActions,
  ImageButton,
  HiddenFileInput,
} from './styles';

// Common activities offered in the workout type typeahead, alongside the
// user's own external programs.
const COMMON_WORKOUT_TYPES = [
  'Running',
  'Walking',
  'Hiking',
  'Cycling',
  'Swimming',
  'Rowing',
  'Weightlifting',
  'Kettlebells',
  'Climbing',
  'Bouldering',
  'Yoga',
  'Pilates',
  'Stretching',
  'Martial arts',
  'Boxing',
  'Dance',
  'Basketball',
  'Soccer',
  'Tennis',
  'Surfing',
  'Skiing',
  'Skateboarding',
];

type Suggestion = {
  label: string;
  isProgram?: boolean;
};

// The same ease scale the in-session Ponder asks, worded for any activity.
export const EASE_RATING = RATINGS.find(({ type }) => type === 'ease')!;
export const EASE_ORDER = EASE_RATING.options.map(({ value }) => value);

// Local YYYY-MM-DD for <input type="date" /> (toISOString would shift the day
// for users behind UTC)
export const toDateInputValue = (date: Date) => {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

// Send the picked date as local noon so it lands on the same calendar day in
// every reasonable timezone
export const toCompletedAt = (dateInputValue: string) => {
  const [year, month, day] = dateInputValue.split('-').map(Number);
  return new Date(year, month - 1, day, 12).toISOString();
};

type WorkoutTypeInputProps = {
  name: string;
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onBlur: (event: FocusEvent<HTMLInputElement>) => void;
  onSelect: (label: string) => void;
  autoFocus?: boolean;
  className?: string;
};

/** Free-text workout type with suggestions from owned external programs and common activities. */
export const WorkoutTypeInput = ({
  name,
  value,
  onChange,
  onBlur,
  onSelect,
  autoFocus,
  className,
}: WorkoutTypeInputProps) => {
  const dispatch = useDispatch();
  const programs = useSelector((state: StoreType) => state.programs.all);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // Owned external programs feed the typeahead; the saga skips the request
  // when programs are already in the store
  useEffect(() => {
    dispatch(fetchPrograms());
  }, [dispatch]);

  const allSuggestions = useMemo<Suggestion[]>(() => {
    const externalPrograms = (programs || [])
      .filter(({ isExternal }) => isExternal)
      .map(({ name: label }) => ({ label, isProgram: true }));

    const programNames = new Set(externalPrograms.map(({ label }) => label.toLowerCase()));

    return [
      ...externalPrograms,
      ...COMMON_WORKOUT_TYPES.filter((label) => !programNames.has(label.toLowerCase())).map(
        (label) => ({ label }),
      ),
    ];
  }, [programs]);

  const suggestions = useMemo(() => {
    const normalized = value.trim().toLowerCase();

    if (!normalized) {
      return allSuggestions;
    }

    return allSuggestions.filter(
      ({ label }) => label.toLowerCase().includes(normalized) && label.toLowerCase() !== normalized,
    );
  }, [allSuggestions, value]);

  const close = () => {
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const select = (suggestion: Suggestion) => {
    onSelect(suggestion.label);
    close();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || !suggestions.length) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedIndex((index) => (index + 1) % suggestions.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex((index) => (index - 1 + suggestions.length) % suggestions.length);
    } else if (event.key === 'Enter' && highlightedIndex >= 0) {
      event.preventDefault();
      select(suggestions[highlightedIndex]);
    } else if (event.key === 'Escape') {
      close();
    }
  };

  return (
    <TypeaheadWrapper className={className}>
      <TextInput
        name={name}
        aria-label="Type of workout"
        value={value}
        onChange={(event) => {
          onChange(event);
          setIsOpen(true);
          setHighlightedIndex(-1);
        }}
        onFocus={() => setIsOpen(true)}
        onBlur={(event) => {
          onBlur(event);
          close();
        }}
        onKeyDown={handleKeyDown}
        placeholder="e.g. Running, bouldering, yoga class…"
        maxLength={120}
        autoComplete="off"
        autoFocus={autoFocus}
      />
      {isOpen && suggestions.length > 0 && (
        <SuggestionsList>
          {suggestions.map((suggestion, index) => (
            <SuggestionItem
              key={suggestion.label}
              type="button"
              highlighted={index === highlightedIndex}
              // preventDefault keeps focus on the input so blur
              // doesn't close the list before the click lands
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => select(suggestion)}
            >
              {suggestion.label}
              {suggestion.isProgram && <SuggestionHint>Program</SuggestionHint>}
            </SuggestionItem>
          ))}
        </SuggestionsList>
      )}
    </TypeaheadWrapper>
  );
};

export type PhotoPicker = ReturnType<typeof usePhotoPicker>;

/**
 * Local state for an optional workout photo. `initialPreview` shows a photo that
 * is already saved; `isRemoved` says the user took that saved photo away.
 */
export const usePhotoPicker = (initialPreview: string | null = null) => {
  const [formData, setFormData] = useState<FormData | null>(null);
  const [preview, setPreview] = useState<string | null>(initialPreview);
  const [isRemoved, setIsRemoved] = useState(false);
  const objectUrl = useRef<string | null>(null);

  const revoke = () => {
    if (objectUrl.current) {
      URL.revokeObjectURL(objectUrl.current);
      objectUrl.current = null;
    }
  };

  useEffect(() => revoke, []);

  const pick = useCallback((file: File) => {
    const data = new FormData();
    data.set('file', file);

    revoke();
    objectUrl.current = URL.createObjectURL(file);
    setFormData(data);
    setPreview(objectUrl.current);
    setIsRemoved(false);
  }, []);

  const remove = useCallback(() => {
    revoke();
    setFormData(null);
    setPreview(null);
    setIsRemoved(true);
  }, []);

  return { formData, preview, isRemoved, pick, remove };
};

export const PhotoField = ({ photo }: { photo: PhotoPicker }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    if (file) {
      photo.pick(file);
    }
  };

  const handleRemove = () => {
    photo.remove();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <ImageRow>
      {photo.preview && <ImagePreview src={photo.preview} alt="Workout photo preview" />}
      <ImageActions>
        <ImageButton type="button" onClick={() => fileInputRef.current?.click()}>
          {photo.preview ? 'Change photo' : 'Add a photo'}
        </ImageButton>
        {photo.preview && (
          <ImageButton type="button" onClick={handleRemove}>
            Remove photo
          </ImageButton>
        )}
      </ImageActions>
      <HiddenFileInput
        ref={fileInputRef}
        type="file"
        accept="image/*"
        aria-label="Workout photo"
        onChange={handleChange}
      />
    </ImageRow>
  );
};
